import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import { searchErrorHandler } from "utils/formValidation";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import cardTypeOptions from "utils/cardTypeOptions/CardTypeOptions.jsx";
import cardOriginOptions from "utils/cardOriginOptions/CardOriginOptions.jsx";
import statusOptions from "utils/statusOptions/StatusOptions.jsx";
import s from "./ChangeLog.module.scss";
import { queryList } from "./model";
import { useSnackbar } from "notistack";
import { queryTableData } from "../apiUtils";

const ChangeLog = ({ path }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "SettlementPlanChangeLog";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState({
    id: "",
    merchantId: "",
    merchantName: "",
    bankAccount: "",
    beneficiary: "",
    currency: "",
    totalAmount: "",
    settleCurrency: "",
    adjustAmount: "",
    totalNet: "",
    startDate: null,
    endDate: null,
    startSettleDate: null,
    endSettleDate: null,
    cardType: "",
    cardOrigin: "",
    status: "",
  });
  const [focus, setFocus] = useState({});
  const [error, setError] = useState({
    startDate: false,
    endDate: false,
    startDateLaterThanEndDate: false,
    startSettleDate: false,
    endSettleDate: false,
    settlementStartDateLaterThanEndDate: false,
  });

  const [columnsArray, setColumnsArray] = useState([]);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse == undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const handleQueryListUIResponse = (apiResponse) => {
    if (!apiResponse || !apiResponse?.data) {
      return snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.settlement.plan.sectionName")}` }), "error");
    }
    const { status, data: responseBody } = apiResponse;

    if (status === 400) {
      snackbar(
        t("general.message.search.error") + ": " + responseBody.message,
        "error",
      );
      setDatasource({ data: [], page: 0, total: 0 });
      setCurrentPage(0);
      setPageSize(20);
      Object.assign(pageSizeInfoRef.current, {
        currentpage: 0,
        pagesize: 20,
      });
      return;
    }

    if (status === 401) {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    }

    if (status === 403) {
      snackbar(t("general.message.unauthorized"), "error");
      return;
    }

    switch (responseBody.responseCode) {
      case "0000":
        if (responseBody?.data?.length === 0) {
          snackbar(t("general.message.no.result"), "warning");
        }
        setDatasource(responseBody);
        setCurrentPage(responseBody.page);
        setPageSize(responseBody.size);
        Object.assign(pageSizeInfoRef.current, {
          currentpage: responseBody.page,
          pagesize: responseBody.size,
        });
        break;

      default:
        snackbar(t("general.message.networkError"), "error");
        break;
    }
  };

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryTableData(queryList, {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    handleQueryListUIResponse(apiResponse);
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      id: "",
      merchantId: "",
      merchantName: "",
      bankAccount: "",
      beneficiary: "",
      currency: "",
      totalAmount: "",
      settleCurrency: "",
      adjustAmount: "",
      totalNet: "",
      startDate: null,
      endDate: null,
      startSettleDate: null,
      endSettleDate: null,
      cardType: "",
      cardOrigin: "",
      status: "",
    });
  };

  const resetError = () => {
    setError({
      startDate: false,
      endDate: false,
      startDateLaterThanEndDate: false,
      startSettleDate: false,
      endSettleDate: false,
      settlementStartDateLaterThanEndDate: false,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    resetError();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            startDateLaterThanEndDate: true,
          },
        (input) =>
          ((input.startSettleDate && !input.endSettleDate) ||
            input.endSettleDate === "") && { endSettleDate: true },
        (input) =>
          ((input.endSettleDate && !input.startSettleDate) ||
            input.startSettleDate === "") && { startSettleDate: true },
        (input) =>
          moment(input.startSettleDate).isAfter(input.endSettleDate) && {
            settlementStartDateLaterThanEndDate: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryTableData(queryList, payload);
    handleQueryListUIResponse(apiResponse);
    setSearch(true);
    setSearching(false);
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const transactionDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startDate: false,
        startDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      setError({ ...error, endDate: false, startDateLaterThanEndDate: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    } else {
      return;
    }
  };
  const settlementDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startSettleDate: false,
        settlementStartDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startSettleDate: date });
    } else if (type === "end") {
      setError({
        ...error,
        endSettleDate: false,
        settlementStartDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, endSettleDate: date });
    } else {
      return;
    }
  };
  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const columns = [
    {
      index: 0,
      key: "id",
      title: t("page.finance.settlement.plan.change.log.column.title.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "merchantId",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.merchant.id",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "merchantName",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.merchant.name",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "bankAccount",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.bank.account",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "beneficiary",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.beneficiary",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "currency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "totalAmount",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.total.amount",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 7,
      key: "fxRate",
      title: t("page.finance.settlement.plan.change.log.column.title.fx.rate"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 8,
      key: "totalCost",
      currencyKey: "currency",
      title: t("page.finance.settlement.plan.change.log.column.title.cost"),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 9,
      key: "allFee1",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.all.fee.1",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 10,
      key: "totalServiceFee",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.service.fee",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 11,
      key: "totalChangeFee",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.foreigh.card.fee",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 12,
      key: "totalCampaignFee",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.campaign.fee",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 13,
      key: "allFee2",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.all.fee.2",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 14,
      key: "gatewayFee",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.gateway.fee",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 15,
      key: "threeDSFee",
      currencyKey: "settleCurrency",
      title: t("page.finance.settlement.plan.change.log.column.title.3ds.fee"),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 16,
      key: "tokenFee",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.tokenization.fee",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 17,
      key: "totalTransFee",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.small.ticket.fee",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 18,
      key: "adjustAmount",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.adjust.amount",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 19,
      key: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.settlement.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 20,
      key: "totalNet",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.net.amount",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 21,
      key: "totalNetInternal",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.net.amount.internal",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 22,
      key: "transDate",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.transaction.date",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 23,
      key: "settleDate",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.settlement.date",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 24,
      key: "cardType",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.card.type",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 25,
      key: "cardOrigin",
      title: t(
        "page.finance.settlement.plan.change.log.column.title.card.origin",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 26,
      key: "changeReason",
      title: t("page.finance.settlement.plan.change.log.column.title.reason"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 27,
      key: "status",
      title: t("page.finance.settlement.plan.change.log.column.title.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    const statusList = ["approved", "waiting", "rejected"];
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t("page.finance.settlement.plan.change.log.id.label")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.merchant.id.label",
            )}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.merchant.name.label",
            )}
            value={searchFieldInput.merchantName}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName")}
            sxParams={{ textFocus: focus.merchantName }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.bank.account.label",
            )}
            value={searchFieldInput.bankAccount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "bankAccount")}
            onfocusFn={() => textInputOnFocus("bankAccount")}
            onblurFn={() => textInputOnBlur("bankAccount")}
            sxParams={{ textFocus: focus.bankAccount }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.beneficiary.label",
            )}
            value={searchFieldInput.beneficiary}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "beneficiary")}
            onfocusFn={() => textInputOnFocus("beneficiary")}
            onblurFn={() => textInputOnBlur("beneficiary")}
            sxParams={{ textFocus: focus.beneficiary }}
          />
          <Selection
            label={t(
              "page.finance.settlement.plan.change.log.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currency}
            onchangeFn={(e) => textInputOnchange(e, "currency")}
            onfocusFn={() => textInputOnFocus("currency")}
            onblurFn={() => textInputOnBlur("currency")}
            options={transactionCurrencyOptions("all")}
            sxParams={{ selectionFocus: focus.currency }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.total.amount.label",
            )}
            value={searchFieldInput.totalAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "totalAmount")}
            onfocusFn={() => textInputOnFocus("totalAmount")}
            onblurFn={() => textInputOnBlur("totalAmount")}
            sxParams={{ textFocus: focus.totalAmount }}
          />
          <Selection
            label={t(
              "page.finance.settlement.plan.change.log.settlement.currency.label",
            )}
            selectionValue={searchFieldInput.settleCurrency}
            onchangeFn={(e) => textInputOnchange(e, "settleCurrency")}
            onfocusFn={() => textInputOnFocus("settleCurrency")}
            onblurFn={() => textInputOnBlur("settleCurrency")}
            options={transactionCurrencyOptions(["HKD", "USD"])}
            sxParams={{ selectionFocus: focus.settleCurrency }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.adjust.amount.label",
            )}
            value={searchFieldInput.adjustAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "adjustAmount")}
            onfocusFn={() => textInputOnFocus("adjustAmount")}
            onblurFn={() => textInputOnBlur("adjustAmount")}
            sxParams={{ textFocus: focus.adjustAmount }}
          />
          <OutlinedText
            label={t(
              "page.finance.settlement.plan.change.log.net.amount.label",
            )}
            value={searchFieldInput.totalNet}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "totalNet")}
            onfocusFn={() => textInputOnFocus("totalNet")}
            onblurFn={() => textInputOnBlur("totalNet")}
            sxParams={{ textFocus: focus.totalNet }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.change.log.transaction.start.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.change.log.transaction.start.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.change.log.transaction.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.change.log.transaction.end.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.change.log.transaction.end.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.change.log.transaction.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError: error.endDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.change.log.settlement.start.date.label",
            )}
            errorMessage={
              error.settlementStartDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.change.log.settlement.start.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.change.log.settlement.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startSettleDate}
            limitValue={searchFieldInput.endSettleDate}
            onchangeFn={settlementDateOnChange}
            onfocusFn={() => textInputOnFocus("startSettleDate")}
            onblurFn={() =>
              textInputOnBlur("startSettleDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.startSettleDate,
              datepickerError:
                error.startSettleDate ||
                error.settlementStartDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.change.log.settlement.end.date.label",
            )}
            errorMessage={
              error.settlementStartDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.change.log.settlement.end.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.change.log.settlement.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endSettleDate}
            limitValue={searchFieldInput.startSettleDate}
            onchangeFn={settlementDateOnChange}
            onfocusFn={() => textInputOnFocus("endSettleDate")}
            onblurFn={() =>
              textInputOnBlur("endSettleDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.endSettleDate,
              datepickerError:
                error.endSettleDate ||
                error.settlementStartDateLaterThanEndDate,
            }}
          />
          <Selection
            label={t("page.finance.settlement.plan.change.log.card.type.label")}
            selectionValue={searchFieldInput.cardType}
            onchangeFn={(e) => textInputOnchange(e, "cardType")}
            onfocusFn={() => textInputOnFocus("cardType")}
            onblurFn={() => textInputOnBlur("cardType")}
            options={cardTypeOptions(
              (e) => !["loyalty", "octopus"].includes(e.key),
            )}
            sxParams={{ selectionFocus: focus.cardType }}
          />
          <Selection
            label={t(
              "page.finance.settlement.plan.change.log.card.origin.label",
            )}
            selectionValue={searchFieldInput.cardOrigin}
            onchangeFn={(e) => textInputOnchange(e, "cardOrigin")}
            onfocusFn={() => textInputOnFocus("cardOrigin")}
            onblurFn={() => textInputOnBlur("cardOrigin")}
            options={cardOriginOptions("all")}
            sxParams={{ selectionFocus: focus.cardOrigin }}
          />
          <Selection
            label={t("page.finance.settlement.plan.change.log.status.label")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnchange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={statusOptions(statusList)}
            sxParams={{ selectionFocus: focus.status }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>
              {t("page.finance.settlement.plan.change.log.description.1")}
            </span>
            <span>
              {t("page.finance.settlement.plan.change.log.description.2")}
            </span>
          </div>
          <FunctionalButtonBar
            loading={loading || searching}
            refreshBtn
            handleRefresh={handleSearch}
            columnDisplayBtn
            columnDisplayProps={{
              columns,
              columnsArray,
              setColumnsArray,
              rememberPreference: "finance-settlement-change-log",
            }}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChangeLog);
